/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback } from "react";
import { Form } from "@unform/web";

import {
    Select,
    MainButton,
    Modal,
    Pagination,
    LocalLoading,
} from "../../../global";
import { useLoading } from "../../../../shared/hooks/useLoading";
import { SearchAnyInputs } from "./search-any-inputs";

import { LayersApiService } from "../../../../shared/services/api/layers-api.service";
import { RecordsAnyTable } from "./components/records-any-table";

interface SearchAnyFormProps {
    possibilites: Record<string, string>;
}

const LIMIT = 20;

export interface DynamicInput {
    column_name: string;
    data_type: string;
    is_nullable: string;
    character_maximum_length: number | null;
    numeric_precision: number | null;
    column_default: number | null;
    table_name?: string;
}

export const SearchAnyForm: React.FC<SearchAnyFormProps> = ({
    possibilites,
}) => {
    const { setLoading } = useLoading();
    const [localLoading, setLocalLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [records, setRecords] = useState<any[]>([]);
    const [recordsAmount, setRecordsAmount] = useState(0);
    const [filters, setFilters] = useState<Record<string, string>>();
    const [dynamicInputs, setDynamicInputs] = useState<DynamicInput[]>([]);
    const [selectedTable, setSelectedTable] = useState<string | null>(null);

    // Transformar possibilites em options do Select
    const options = Object.entries(possibilites).map(([key, label]) => ({
        value: key,
        label,
    }));

    const toggleModalState = useCallback(
        () => setShowModal((prev) => !prev),
        []
    );

    // Busca registros da tabela
    const fetchRecords = useCallback(
        async (
            page: number,
            form?: Record<string, string>,
            tableName?: string
        ) => {
            if (!tableName) {
                setRecords([]);
                setRecordsAmount(0);
                return;
            }

            const where = form;
            const query = `offset=${(page - 1) * LIMIT}&limit=${LIMIT}&where=${JSON.stringify(where)}`;

            try {
                const response = await LayersApiService.getTableDataLayers(
                    tableName.trim(),
                    query
                );

                const data: any[] = (response as any).data || response || [];
                const total: number =
                    response.paginate.total ??
                    response.paginate?.total ??
                    data.length;
                setRecords(data);
                setRecordsAmount(total);
            } catch (error) {
                console.error("Erro ao buscar:", error);
                setRecords([]);
                setRecordsAmount(0);
            }
        },
        []
    );

    const handleQueryPaginate = useCallback(
        async (page: number) => {
            if (!selectedTable) return;

            try {
                setLocalLoading(true);
                await fetchRecords(page, filters, selectedTable);
            } finally {
                setLocalLoading(false);
            }
        },
        [selectedTable, filters, fetchRecords]
    );

    const handleSubmit = useCallback(
        async (form: Record<string, string>) => {
            if (!selectedTable) {
                console.warn("Nenhuma tabela selecionada.");
                return;
            }

            setLoading(true);
            try {
                setFilters(form);
                await fetchRecords(1, form, selectedTable);
                toggleModalState();
            } finally {
                setLoading(false);
            }
        },
        [selectedTable, fetchRecords, setLoading, toggleModalState]
    );

    // Seleção do Select (dentro do Form apenas para manter Unform funcionando)
    const handleSelectChange = useCallback(async (newValue: any) => {
        const option = Array.isArray(newValue) ? newValue[0] : newValue;

        if (!option) {
            setDynamicInputs([]);
            setSelectedTable(null);
            return;
        }

        const valueStr: string = option.value;
        const parts = valueStr.split(":");
        const tableName = parts.pop() ?? "";

        setSelectedTable(tableName);
        setLocalLoading(true);
        try {
            const response = await LayersApiService.getTableLayers(tableName);

            const inputsWithTable = (response || []).map(
                (input: DynamicInput) => ({
                    ...input,
                    table_name: tableName,
                })
            );
            setDynamicInputs(inputsWithTable);
        } finally {
            setLocalLoading(false);
        }
    }, []);

    return (
        <>
            <div className="flex flex-col justify-center items-center mt-4">
                <div className="w-96 h-96">
                    <Form onSubmit={handleSubmit}>
                        <Select
                            name="_tableSelect"
                            label="CAMADA"
                            placeholder="Clique para selecionar..."
                            options={options}
                            isClearable
                            onChange={handleSelectChange}
                        />

                        {dynamicInputs.length > 0 ? (
                            <>
                                {dynamicInputs.map((field) => (
                                    <SearchAnyInputs
                                        input={field.column_name}
                                        key={field.column_name}
                                    />
                                ))}
                                <MainButton
                                    type="submit"
                                    style={{ marginTop: "1rem" }}
                                >
                                    BUSCAR
                                </MainButton>
                            </>
                        ) : (
                            <p style={{ margin: "1rem 0", color: "#555" }}>
                                Nenhum campo disponível. Selecione uma tabela
                                habilitada para carregar os campos.
                            </p>
                        )}
                    </Form>
                </div>
            </div>
            {/* Modal */}
            <Modal
                show={showModal}
                handleCloseModal={toggleModalState}
                size="xl"
                title="RESULTADOS DA BUSCA"
            >
                <div className="h-96">
                    {localLoading ? (
                        <LocalLoading />
                    ) : (
                        <RecordsAnyTable
                            records={records}
                            doAfterActionsClick={toggleModalState}
                        />
                    )}
                </div>

                <Pagination
                    totalOfPages={Math.ceil(recordsAmount / LIMIT)}
                    doAfterClick={handleQueryPaginate}
                    totalOfItems={recordsAmount}
                    maxItemsToDisplay={LIMIT}
                />
            </Modal>
        </>
    );
};
